export function analyseRound(r) {
  const scores = {}
  const holes = r.holes || 18
  const fwTotal = r.fw_total || (holes === 9 ? 7 : 14)

  // Driving
  let drivingScore = (1 - r.fw / fwTotal) * 60
  const totalDriverMisses = (r.driver_miss_left || 0) + (r.driver_miss_right || 0)
  const missBias = r.driver_miss_left > r.driver_miss_right ? 'left' : r.driver_miss_right > r.driver_miss_left ? 'right' : 'both'
  if (totalDriverMisses > 0 && missBias === 'both') drivingScore += 20
  else if (totalDriverMisses > 0) drivingScore += 10
  if (r.driver_dist === 'under220') drivingScore += 10
  scores.driving = Math.min(100, Math.round(drivingScore))

  // Approach - use per-direction data if available
  let approachScore = (1 - r.gir / holes) * 60
  const totalMisses = (r.approach_left || 0) + (r.approach_right || 0) + (r.approach_short || 0) + (r.approach_long || 0)
  if (r.approach_disp === 'scattered' || totalMisses > holes * 0.6) approachScore += 20
  else if (r.approach_disp === 'moderate' || totalMisses > holes * 0.3) approachScore += 10
  if ((r.approach_long || 0) > (r.approach_short || 0)) approachScore += 5 // long = penalty risk
  scores.approach = Math.min(100, Math.round(approachScore))

  // Short game
  const udPct = r.ud_att > 0 ? r.ud_made / r.ud_att : 0
  const bunkerPct = r.bunkers > 0 ? r.bunker_saves / r.bunkers : 1
  scores.shortGame = Math.min(100, Math.round((1 - udPct) * 70 + (1 - bunkerPct) * 30))

  // Putting - use 1/2/3 putt data if available
  const totalPutts = r.putts || ((r.one_putts || 0) + (r.two_putts || 0) + (r.three_putts || 0))
  const avgPerHole = totalPutts / holes
  let puttScore = Math.max(0, (avgPerHole - 1.8) * 40)
  puttScore += (r.three_putts || 0) * 8
  puttScore -= (r.one_putts || 0) * 3 // reward 1-putts
  if (r.putt_issue && r.putt_issue !== 'none') puttScore += 8
  scores.putting = Math.min(100, Math.max(0, Math.round(puttScore)))

  const worst = Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0]
  return { scores, worst }
}

export function getInsights(round, scores) {
  const holes = round.holes || 18
  const fwTotal = round.fw_total || (holes === 9 ? 7 : 14)
  const fwPct = Math.round((round.fw / fwTotal) * 100)
  const girPct = Math.round((round.gir / holes) * 100)
  const udPct = round.ud_att > 0 ? Math.round((round.ud_made / round.ud_att) * 100) : 0
  const bPct = round.bunkers > 0 ? Math.round((round.bunker_saves / round.bunkers) * 100) : 0
  const totalPutts = round.putts || ((round.one_putts || 0) + (round.two_putts || 0) + (round.three_putts || 0))

  // Approach direction breakdown
  const appLeft = round.approach_left || 0
  const appRight = round.approach_right || 0
  const appShort = round.approach_short || 0
  const appLong = round.approach_long || 0
  const totalAppMisses = appLeft + appRight + appShort + appLong
  const appBreakdown = totalAppMisses > 0
    ? `Misses: ${appLeft} left, ${appRight} right, ${appShort} short, ${appLong} long.`
    : ''

  // Driver direction breakdown
  const dLeft = round.driver_miss_left || 0
  const dRight = round.driver_miss_right || 0
  const driverBreakdown = (dLeft + dRight) > 0
    ? `${dLeft} left, ${dRight} right.`
    : 'No major misses recorded.'

  return {
    driving: `You hit ${round.fw}/${fwTotal} fairways (${fwPct}%). Miss breakdown: ${driverBreakdown} ${
      dLeft > dRight ? 'Predominantly missing left — check your face angle at impact.' :
      dRight > dLeft ? 'Predominantly missing right — check your grip and face angle.' :
      dLeft === dRight && dLeft > 0 ? 'Missing both ways suggests a swing path inconsistency.' :
      'Great accuracy off the tee.'
    }`,
    approach: `You hit ${round.gir}/${holes} greens (${girPct}%). ${appBreakdown} ${
      appShort > appLong ? 'You\'re predominantly coming up short — try taking one more club on approach.' :
      appLeft > appRight ? 'Missing left is your tendency — check your alignment and club face.' :
      appRight > appLeft ? 'Missing right — work on your swing path and face angle through impact.' :
      girPct >= 60 ? 'Solid iron play this round.' : 'Focus on distance control to hit more greens.'
    }`,
    shortGame: `You converted ${round.ud_made}/${round.ud_att} up & downs (${udPct}%). ${
      udPct >= 60 ? 'Above average — solid around the greens.' :
      udPct >= 40 ? 'A few more conversions per round could save 3–4 shots.' :
      'Below average — short game practice will have the biggest impact on your score.'
    }${round.bunkers > 0 ? ` Sand save rate: ${bPct}%.` : ''}`,
    putting: `${totalPutts} total putts — ${round.one_putts || 0} one-putts, ${round.two_putts || 0} two-putts, ${round.three_putts || 0} three-putts. ${
      (round.three_putts || 0) >= 4 ? 'Three-putts are costing you significantly. Lag putting from distance is the priority.' :
      (round.one_putts || 0) >= 5 ? 'Excellent holing out — keep it up.' :
      totalPutts <= (holes === 9 ? 14 : 28) ? 'Excellent putting round.' :
      totalPutts <= (holes === 9 ? 16 : 32) ? 'Solid on the greens with room to improve.' :
      'Putting cost you shots this round. '
    }${
      round.putt_issue === 'distance' ? 'Distance control from long range is your key fix.' :
      round.putt_issue === 'line' ? 'Line reading practice will help reduce three-putts.' :
      round.putt_issue === 'short' ? 'Develop a consistent pre-putt routine for short putts.' :
      round.putt_issue === 'yips' ? 'Work on your mental routine and pre-shot process.' : ''
    }`
  }
}

export const WEAKNESS_TIPS = {
  driving: 'Your driving is costing you the most shots. Accuracy off the tee sets up everything else. Even hitting one more fairway per round can save 2–3 shots.',
  approach: 'Your approach play is leaking shots. Most amateurs come up short — try taking one more club. Better iron play is the fastest route to lower scores.',
  shortGame: 'Your short game is where your score suffers most. Up & down conversion is the fastest way to drop shots off your handicap. Dedicate 40% of your practice time here.',
  putting: 'Your putting is adding unnecessary strokes. Three-putts are costly — focus on lag putting from 20+ feet to eliminate them and get more tap-ins.'
}

export const AREA_META = {
  driving: { label: 'Driving', icon: '🏌️' },
  approach: { label: 'Approach Play', icon: '🎯' },
  shortGame: { label: 'Short Game', icon: '⛳' },
  putting: { label: 'Putting', icon: '🔵' }
}

export const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
